import fetcher, { API_ENDPOINT } from '@/core/lib/fetcher'
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Group,
  GroupPropertyFormValues,
  GroupSiteFormValues,
  GroupValues,
  Property,
  PropertyFormInputValues,
} from './property.types'
import { APIResponse } from '@/core/types/utils'

export const propertiesQueryOptions = (
  token: string,
  organizationsIds?: string
) =>
  queryOptions({
    queryKey: ['properties', token, organizationsIds],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(
          `${API_ENDPOINT.getProperty}?organizationId=${organizationsIds ?? 'All'}`
        )
        .json<Promise<APIResponse<Property[]>>>(),
  })

export const createProperty = (
  data: PropertyFormInputValues | GroupPropertyFormValues,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + 'property')
    .post(data)
    .json()

export const propertyByIdQueryOptions = (id: string, token: string) =>
  queryOptions({
    queryKey: ['property', id, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + `property/${id}`)
        .json<Promise<{ data: Property }>>(),
  })

export const updateProperty = (
  data: Property,
  siteId: string,
  token: string
) => {
  return fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `property/${siteId}`)
    .put(data)
    .json<Promise<{ data: { id: string } }>>()
}

export const useDeleteProperty = (token: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(import.meta.env.VITE_API_BOOKING + `property/${id}`)
        .delete()
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['properties'],
      })
    },
  })
}

export const createGroup = (data: GroupPropertyFormValues, token: string) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(
      import.meta.env.VITE_API_BOOKING +
        `organizations/${data.siteDetails.organizationId}/groups`
    )
    .post(data)
    .json()

export const getGroupsQueryOptions = (
  token: string,
  organizationsIds?: string
) =>
  queryOptions({
    // organizationsIds must be part of the key: it is a request parameter, so
    // leaving it out made the organization filter serve the previous org's
    // cached list. Existing invalidations use the ['groups'] prefix, which
    // still matches partially.
    queryKey: ['groups', organizationsIds],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(
          import.meta.env.VITE_API_BOOKING +
            `organizations/groups?organizationIds=${organizationsIds ?? 'All'}`
        )
        .json<Promise<APIResponse<Group[]>>>(),
  })

export const getGroupByIdQueryOptions = (id: string, token: string) =>
  queryOptions({
    queryKey: ['group', id, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + `organizations/groups/${id}`)
        .json<Promise<{ data: GroupValues }>>(),
  })

export const useDeleteGroup = (token: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(import.meta.env.VITE_API_BOOKING + `organizations/groups/${id}`)
        .delete()
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['groups'],
      })
    },
  })
}

export const updateGroup = (
  data: GroupValues,
  groupId: string,
  token: string
) => {
  return fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `organizations/groups/${groupId}`)
    .put(data)
    .json<Promise<{ data: { id: string } }>>()
}

export const createSiteGroup = (data: GroupSiteFormValues, token: string) => {
  return fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `groups/site-groups`)
    .post(data)
    .json<Promise<{ data: { id: string } }>>()
}

export const useDeleteSiteGroup = (token: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(import.meta.env.VITE_API_BOOKING + `groups/site-groups/${id}`)
        .delete()
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['groups'],
      })
    },
  })
}

export const useToggleSiteStatus = (token: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (siteId: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(import.meta.env.VITE_API_BOOKING + `property/${siteId}/stop-sell`)
        .put()
        .json<Promise<{ data: { id: string } }>>(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['properties'],
      })
    },
  })
}
